/*
 * Utility: normalizePhoneSearch
 * ----------------------------------
 * Given a raw search input from the user, return an array of possible
 * normalized variants that should match records stored in the backend.
 * If the input is not recognised as a phone number (e.g. contains fewer
 * than 7 digits), the function returns the original input.
 *
 * The main variant returned (index 0) is the digits-only representation
 * of the phone number, which plays nicely with SQL `LIKE` searches
 * regardless of formatting characters such as +, (, ), space or dashes.
 *
 * For valid phone numbers the function also adds:
 *   • E.164 format (e.g. +17143900341)
 *   • National format (e.g. (714) 390-0341)
 *   • International format (e.g. +1 714-390-0341)
 *   • Variant with/without leading US country code (for US numbers)
 *
 * The helper relies on google-libphonenumber which is already a project
 * dependency.
 */

import {
  PhoneNumberFormat as PNF,
  PhoneNumberUtil,
} from 'google-libphonenumber';

// Default region when one cannot be inferred.
const DEFAULT_REGION = 'US';

/**
 * Strip all non-digit characters from a string.
 */
function toDigitsOnly(raw: string): string {
  return raw.replace(/\D/g, '');
}

export function normalizePhoneSearch(
  rawInput: string,
  region: string = DEFAULT_REGION
): string[] {
  if (!rawInput) return [''];

  const phoneUtil = PhoneNumberUtil.getInstance();

  try {
    const number = phoneUtil.parseAndKeepRawInput(rawInput, region);

    // If the parsed number is not considered valid, treat the input as free text.
    if (!phoneUtil.isValidNumber(number)) {
      return [rawInput];
    }

    const variants: Set<string> = new Set();

    // Digits-only form from the canonical E.164 representation
    const e164 = phoneUtil.format(number, PNF.E164);
    const digitsOnly = e164.replace(/\D/g, '');
    variants.add(digitsOnly);

    // Full E.164 format (e.g. +17143900341)
    variants.add(e164);

    // National format stripped of non-digits
    const national = phoneUtil.format(number, PNF.NATIONAL).replace(/\D/g, '');
    variants.add(national);

    // International format stripped of non-digits
    const intlDigits = phoneUtil
      .format(number, PNF.INTERNATIONAL)
      .replace(/\D/g, '');
    variants.add(intlDigits);

    // For US numbers – include both with and without leading 1
    if (region === 'US' || number.getCountryCode() === 1) {
      if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
        variants.add(digitsOnly.substring(1));
      } else if (digitsOnly.length === 10) {
        variants.add('1' + digitsOnly);
      }
    }

    // Always include the raw input last
    variants.add(rawInput);

    return Array.from(variants);
  } catch (err) {
    // Parsing failed – fall back to original input.
    if (process.env.NODE_ENV === 'development') {
      console.debug('Phone number parsing failed for input:', rawInput, err);
    }
    return [rawInput];
  }
}
